/**
 * Which guest fields a location requires (#35).
 *
 * Mandatory-ness used to be a constant written separately into every guest
 * form, and the forms disagreed: the batch check-in blocked on a missing ID
 * document after the individual form had stopped doing so, so the same guest
 * was acceptable through one door and not through the next.
 *
 * One reader, so every form answers the question the same way. `name` is NOT
 * in the catalogue because it is always required and is not a site's choice.
 *
 * GDPR (Art. 5.1.c): each entry is a data-MINIMISATION decision, not a UX one
 * — requiring a field obliges the site to collect it from every guest. An
 * absent or empty list means name only, which is the behaviour that existed
 * before the setting, so a location that never opens the screen is unaffected.
 */

import type { GuestFormField, Location } from '../types';

/** The subset of a person a guest form can be asked to require. */
export interface GuestFieldValues {
  documentType?: string | null;
  documentNumber?: string | null;
  documentExpiration?: string | null;
  birthDate?: string | null;
  nationality?: string | null;
  nationalityIso?: string | null;
  email?: string | null;
  phone?: string | null;
}

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Is `field` satisfied by `values`?
 *
 * `document` needs BOTH halves: a number with no type cannot be checked
 * against anything, and a type with no number is not a document. The other
 * fields are a single value.
 */
export function isGuestFieldPresent(
  field: GuestFormField,
  values: GuestFieldValues | null | undefined,
): boolean {
  if (!values) return false;
  switch (field) {
    case 'document':
      return hasText(values.documentType) && hasText(values.documentNumber);
    case 'document_expiration':
      return hasText(values.documentExpiration);
    case 'birth_date':
      return hasText(values.birthDate);
    case 'nationality':
      return hasText(values.nationality) || hasText(values.nationalityIso);
    case 'email':
      return hasText(values.email);
    case 'phone':
      return hasText(values.phone);
    default:
      return true;
  }
}

/** The fields this location requires. Empty for a location still loading. */
export function requiredGuestFields(
  location: Pick<Location, 'guestFormRequiredFields'> | null | undefined,
): GuestFormField[] {
  return location?.guestFormRequiredFields ?? [];
}

/** `true` when the location requires `field`. */
export function isGuestFieldRequired(
  location: Pick<Location, 'guestFormRequiredFields'> | null | undefined,
  field: GuestFormField,
): boolean {
  return requiredGuestFields(location).includes(field);
}

/**
 * Required fields this person is missing, in catalogue order.
 *
 * Empty means the person satisfies everything the site asks for. A location
 * that is still loading requires nothing — a form must not block an operator
 * because a settings fetch has not landed yet.
 */
export function missingRequiredGuestFields(
  location: Pick<Location, 'guestFormRequiredFields'> | null | undefined,
  values: GuestFieldValues | null | undefined,
): GuestFormField[] {
  return requiredGuestFields(location).filter(
    (field) => !isGuestFieldPresent(field, values),
  );
}
